// The left column's list that isn't the contents (RoomShell.swift's LeftColumn): the Library's projects
// (`ProjectsColumn`: title, then kind · words, or how long it has rested). A page can bring its own column instead
// (rooms/registry.ts `column`; Plan's story pages bring views/story/StoryColumn.tsx).
import { useMemo, useState } from 'react';
import type { ProjectType } from '@writing-suite/core';
import { projectDetail } from '../../../core/src/home/index.ts';
import { projectSeries, projectTitle, type LibraryProject } from '../store/library.ts';
import { seriesGroups } from '../views/helpers.ts';
import { sortProjects } from '../views/Library.tsx';
import { useApp } from './context.ts';
import { followLink, formatRoute } from './route.ts';

export function projectRowDetail(p: LibraryProject, now = new Date()): string {
  const modified = p.summary?.modifiedAt ? new Date(p.summary.modifiedAt) : null;
  return projectDetail({ type: (p.info.type ?? p.summary?.type ?? 'manuscript') as ProjectType, words: p.info.words ?? p.summary?.words ?? null, modifiedAt: modified }, now);
}

/** The Library's column: every project on the web, searched by title or series; choosing one shows its Home. */
export function ProjectsColumn({ currentId, page, onChoose }: { currentId: string | null; page: string; onChoose?: () => void }) {
  const app = useApp();
  const [query, setQuery] = useState('');
  const all = app.projects ? sortProjects(app.projects) : [];
  const words = query.trim().toLowerCase();
  const shown = words ? all.filter((p) => projectTitle(p).toLowerCase().includes(words) || (projectSeries(p) ?? '').toLowerCase().includes(words)) : all;
  const groups = seriesGroups(shown, projectSeries);
  const target = page === 'statistics' ? 'statistics' : 'home';
  return (
    <div className="projects-column">
      {all.length > 1 ? (
        <label className="column-search">
          <span className="visually-hidden">Search projects</span>
          <input type="search" placeholder="Search projects" value={query} onChange={(e) => setQuery(e.target.value)} />
        </label>
      ) : null}
      {shown.length < all.length ? (
        <p className="column-note">
          {shown.length ? `Showing ${shown.length} of ${all.length}` : 'No projects match'}{' '}
          <button type="button" className="link-button" onClick={() => setQuery('')}>
            Show All
          </button>
        </p>
      ) : null}
      {groups.map((g) => (
        <section key={g.series ?? ''} className="column-group" aria-label={g.series ? `Series: ${g.series}` : groups.length > 1 ? 'Not in a series' : 'Projects'}>
          {g.series ? <h2 className="eyebrow column-eyebrow">{g.series}</h2> : groups.length > 1 ? <h2 className="eyebrow column-eyebrow">Not in a series</h2> : null}
          <ul className="column-list">
            {g.items.map((p) => {
              const href = formatRoute({ name: 'project', projectId: p.id, room: 'library', page: target, nodeId: null, arrangementId: null, params: {} });
              const current = p.id === currentId;
              return (
                <li key={p.id} data-column-project={p.id}>
                  <a
                    className={`column-row${current ? ' selected' : ''}`}
                    href={href}
                    aria-current={current ? 'true' : undefined}
                    onClick={(e) => {
                      onChoose?.();
                      followLink(e, href);
                    }}
                  >
                    <span className="column-row-title">{projectTitle(p)}</span>
                    <span className="column-row-detail">{projectRowDetail(p)}</span>
                  </a>
                </li>
              );
            })}
          </ul>
        </section>
      ))}
      <p className="column-foot">
        <a href="/" onClick={(e) => followLink(e, '/')}>
          All projects
        </a>
      </p>
    </div>
  );
}
